import React, { useCallback, useMemo, useRef, useState } from "react"
import {
  FlatList,
  StyleSheet,
  Text,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from "react-native"
import { Portal } from "react-native-portalize"
import Ionicon from "react-native-vector-icons/Ionicons"
import MaterialIcon from "react-native-vector-icons/MaterialIcons"

import Constants, { colours, fonts } from "../../base/Constants"

const DropdownSelect = ({
  selectedValue,
  onChange,
  defaultLabel = "Choose...",
  options,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [measurements, setMeasurements] = useState()
  const ref = useRef()

  const onOptionPress = useCallback(
    value => () => {
      if (onChange) {
        onChange(value)
      }

      setIsOpen(false)
      setMeasurements()
    },
    [onChange],
  )

  const close = useCallback(() => {
    setIsOpen(false)
    setMeasurements()
  }, [])

  const toggleOpen = useCallback(
    _open => () => {
      if (_open) {
        ref.current?.measureInWindow((left, top) => {
          const safeHeight = Constants.window.height - top - 50
          setMeasurements({
            top: top - 4,
            left,
            maxHeight: Math.min(safeHeight, 500),
          })
          setIsOpen(true)
        })
      } else {
        close()
      }
    },
    [close],
  )

  const selectedOption = useMemo(
    () => options?.find(option => option?.value === selectedValue),
    [options, selectedValue],
  )

  const keyExtractor = useCallback(item => item.value, [])

  const renderOption = useCallback(
    ({ item: option }) => {
      const isSelected = selectedValue === option?.value

      return (
        <TouchableOpacity
          key={option?.value}
          onPress={onOptionPress(option?.value)}
          style={[
            styles.dropdownItem,
            !!isSelected && styles.dropdownItemSelected,
          ]}
        >
          <Text style={styles.dropdownItemTitle}>{option?.label}</Text>
          <MaterialIcon
            name={`radio-button-${isSelected ? "checked" : "unchecked"}`}
            size={24}
            color={colours.appRed}
            style={styles.itemIcon}
          />
        </TouchableOpacity>
      )
    },
    [onOptionPress, selectedValue],
  )

  return (
    <View ref={ref} style={styles.labelWrap} collapsable={false}>
      <TouchableOpacity
        onPress={toggleOpen(!isOpen)}
        style={[styles.dropdownItem, styles.dropdownItemSelected]}
        disabled={!options?.length}
      >
        <Text style={styles.dropdownItemTitle}>
          {selectedOption?.label || defaultLabel}
        </Text>
        <Ionicon
          name="ios-arrow-down"
          size={24}
          color={colours.appRed}
          style={styles.labelIcon}
        />
      </TouchableOpacity>
      {!!isOpen && !!measurements && (
        <Portal>
          <TouchableWithoutFeedback onPress={toggleOpen(false)}>
            <View style={styles.tapOutsideContainer}>
              <View
                style={[
                  styles.dropdownMenu,
                  { top: measurements.top, left: measurements.left },
                ]}
              >
                <FlatList
                  keyExtractor={keyExtractor}
                  style={{ maxHeight: measurements.maxHeight }}
                  data={options}
                  renderItem={renderOption}
                  bounces={false}
                />
              </View>
            </View>
          </TouchableWithoutFeedback>
        </Portal>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  labelWrap: {
    minWidth: 120,
  },
  dropdownItem: {
    paddingHorizontal: 8,
    minHeight: 32,
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "row",
  },
  dropdownItemSelected: {
    backgroundColor: colours.appGreenGrey,
  },
  dropdownItemTitle: {
    fontFamily: fonts.appSemiBold,
    color: colours.appBlack,
    fontSize: 12,
    letterSpacing: 0,
    marginRight: 8,
  },
  tapOutsideContainer: {
    flex: 1,
    backgroundColor: "transparent",
  },
  dropdownMenu: {
    paddingVertical: 4,
    position: "absolute",
    backgroundColor: colours.appWhite,
    borderRadius: 6,
    shadowColor: "rgba(0, 0, 0, 0.5)",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowRadius: 4,
    shadowOpacity: 1,
    elevation: 3,
  },
  labelIcon: {
    marginHorizontal: 5,
  },
  itemIcon: {
    marginHorizontal: 2,
  },
})

export default DropdownSelect
